import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { ButtonPro, Typography } from 'components';
import { agent } from 'services/service';
import { HrManagerSetting } from 'services/HrManagerService';
import { toast } from 'react-toastify';
import './index.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const HrManagerForm = () => {
  const navigate = useNavigate();
  const [setting, setSetting] = useState<HrManagerSetting | null>(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSetting = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await agent.HrManagerService.get();
      setSetting(response.data);
      setEmail(response.data.email ?? '');
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSetting();
  }, [loadSetting]);

  const trimmed = email.trim();
  const isDirty = trimmed !== (setting?.email ?? '');

  const handleSave = async () => {
    if (!trimmed) {
      setError('HR manager email is required.');
      return;
    }

    if (!EMAIL_PATTERN.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await agent.HrManagerService.update(trimmed);
      const { setting: updated } = response.data;

      setSetting(updated);
      setEmail(updated.email ?? '');

      toast.success(
        `HR manager updated. ${
          updated.employeeName ?? updated.email
        } can now approve all pending leave requests.`
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        'Could not update the HR manager. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEmail(setting?.email ?? '');
    setError(null);
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="hr-manager__loading">
        <CircularProgress size={24} />
        <Typography type="small_regular" text="Loading HR manager..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="hr-manager__loading">
        <Typography type="small_regular" text="Could not load the HR manager." />
        <ButtonPro label="Retry" onClick={loadSetting} />
      </div>
    );
  }

  return (
    <div className="hr-manager">
      <section className="hr-manager__card">
        <Typography type="heading5" text="HR Manager" />
        <Typography
          type="small_regular"
          text="This person approves leave requests, manages employee records and leave allocations, and receives all HR email notifications."
        />

        <label className="hr-manager__label" htmlFor="hr-manager-email">
          Email address
        </label>
        <input
          id="hr-manager-email"
          className="hr-manager__input"
          type="email"
          value={email}
          placeholder="name@aventude.com"
          disabled={saving}
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
        />

        {setting?.employeeName && !isDirty && (
          <span className="hr-manager__resolved">
            Currently assigned to {setting.employeeName}
          </span>
        )}

        {error && <span className="hr-manager__error">{error}</span>}

        <span className="hr-manager__hint">
          Pending leave approvals always follow whoever is set here.
        </span>

        <div className="hr-manager__actions">
          <ButtonPro
            label="Cancel"
            type="white"
            onClick={handleCancel}
            disabled={saving}
          />
          <ButtonPro
            label={saving ? 'Saving...' : 'Save'}
            onClick={handleSave}
            disabled={!isDirty || saving}
          />
        </div>
      </section>
    </div>
  );
};
